/**
 * SwimmerContactsDialog — a small popup with parent + emergency contact
 * (click-to-call) and medical info. On the coach-facing attendance / meet-roster
 * surfaces the WHOLE swimmer card is the tap target (bigger than the name alone):
 * drive `open` from the card's onClick and render this alongside the card. The
 * card's interactive controls (attendance toggles, Edit, event chips) call
 * stopPropagation so they don't also open this.
 *
 * Medical info shows here AND inline on the roster row (safety-critical — never
 * hide it behind a tap).
 */
import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';

export interface SwimmerContactInfo {
  parent_name?: string | null;
  parent_phone?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  allergies?: string | null;
  medical_conditions?: string | null;
}

/**
 * Parents commonly type "N/A", "none", "—" etc. as a placeholder when they have
 * no medical info to enter. Treating those literally would fire the red Medical
 * alert on every coach roster row — exactly the kind of alert fatigue that
 * trains coaches to ignore the icon. This helper returns null for those
 * placeholders so they don't trigger the alert. Substantive entries like
 * "No known allergies" or "Mild asthma" pass through unchanged.
 */
const MEDICAL_PLACEHOLDERS = new Set([
  '', 'n/a', 'na', 'n.a.', 'none', 'no', 'nil', '-', '—', '–', '..', '...', '?',
]);
export function meaningfulMedical(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Strip trailing punctuation (so "n/a." / "none." also normalize).
  const normalized = trimmed.replace(/[.!?]+$/g, '').toLowerCase();
  if (MEDICAL_PLACEHOLDERS.has(normalized)) return null;
  return trimmed;
}

/** True iff the swimmer has substantive (non-placeholder) medical info to flag. */
export function hasMeaningfulMedical(contact: SwimmerContactInfo): boolean {
  return !!meaningfulMedical(contact.allergies) || !!meaningfulMedical(contact.medical_conditions);
}

interface Props {
  open: boolean;
  onClose: () => void;
  firstName: string;
  lastName: string;
  age?: number | null;
  contact: SwimmerContactInfo;
}

const SwimmerContactsDialog: React.FC<Props> = ({ open, onClose, firstName, lastName, age, contact }) => {
  const fullName = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  const hasContact =
    !!contact.parent_phone || !!contact.emergency_contact_phone || !!contact.parent_name || !!contact.emergency_contact_name;
  // Filter out N/A-style placeholders so "no info entered" doesn't pop a red alert.
  const allergies = meaningfulMedical(contact.allergies);
  const medicalConditions = meaningfulMedical(contact.medical_conditions);
  const hasMedical = !!allergies || !!medicalConditions;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      // This dialog is rendered inside the clickable swimmer card. React events
      // bubble through the COMPONENT tree, not the DOM portal — so without this a
      // click on the X / backdrop / phone link bubbles up to the card's onClick
      // and instantly re-opens the dialog (the X appears dead). Contain all
      // clicks originating inside the dialog here.
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle sx={{ pr: 5 }}>
        {fullName || '—'}
        {age != null && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
            age {age}
          </Typography>
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {!hasContact && !hasMedical && (
          <Alert severity="info">No contact info on file.</Alert>
        )}

        {hasContact && (
          <Stack spacing={1.5}>
            {(contact.parent_name || contact.parent_phone) && (
              <Box>
                <Typography variant="caption" color="text.secondary">Parent / contact</Typography>
                <Typography variant="body2">{contact.parent_name || '—'}</Typography>
                {contact.parent_phone && (
                  <Button
                    startIcon={<PhoneIcon fontSize="small" />}
                    href={`tel:${contact.parent_phone}`}
                    sx={{ textTransform: 'none', mt: 0.25, p: 0, minWidth: 0 }}
                  >
                    {contact.parent_phone}
                  </Button>
                )}
              </Box>
            )}
            {(contact.emergency_contact_name || contact.emergency_contact_phone) && (
              <Box>
                <Typography variant="caption" color="text.secondary">Emergency contact</Typography>
                <Typography variant="body2">{contact.emergency_contact_name || '—'}</Typography>
                {contact.emergency_contact_phone && (
                  <Button
                    color="error"
                    startIcon={<PhoneIcon fontSize="small" />}
                    href={`tel:${contact.emergency_contact_phone}`}
                    sx={{ textTransform: 'none', mt: 0.25, p: 0, minWidth: 0 }}
                  >
                    {contact.emergency_contact_phone}
                  </Button>
                )}
              </Box>
            )}
          </Stack>
        )}

        {hasMedical && (
          <>
            {hasContact && <Divider sx={{ my: 1.5 }} />}
            <Alert severity="error" icon={<WarningIcon fontSize="small" />}>
              {allergies && (
                <Box><strong>Allergies:</strong> {allergies}</Box>
              )}
              {medicalConditions && (
                <Box><strong>Medical:</strong> {medicalConditions}</Box>
              )}
            </Alert>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SwimmerContactsDialog;

// Helper export so a roster row can show a small "Med" chip beside the name when
// allergies/medical conditions exist (we also keep medical inline on the row —
// never hide it behind a tap).
export function SwimmerMedicalChip({ contact }: { contact: SwimmerContactInfo }) {
  if (!hasMeaningfulMedical(contact)) return null;
  return (
    <Chip
      size="small"
      color="error"
      variant="outlined"
      icon={<WarningIcon sx={{ fontSize: 14 }} />}
      label="Med"
      sx={{ height: 20, fontSize: '0.7rem' }}
    />
  );
}
